/**
 * THE BUTTON (P36) — reference §7, first five rows.
 *
 * =========================================================================
 * THE DEFECT THIS COMPONENT EXISTS FOR
 * =========================================================================
 * S2-08, measured on `DRA-2026-00053`: `Compose draft`, `Update status`,
 * `Assign` and `Add note` all rendered in the primary colour with white text —
 * and **three of the four reported `disabled: true`**. They differed from the
 * one live button by `opacity-40` / `opacity-50` and by nothing else. Four
 * buttons that look like the action, one of which is the action.
 *
 * `opacity` is the wrong tool for the job: it says "this is the same control,
 * faded", when what has to be said is "this is not a control right now". So a
 * disabled button here takes a DIFFERENT GROUND — `--head` — and `--faint`
 * text, which is the one thing `--faint` is allowed to be (design.md §2).
 * There is no path through `classesFor` on which a disabled button keeps the
 * brand ground, and `Button.test.tsx` asserts exactly that.
 *
 * Reference §7 adds a condition the kit cannot enforce on its own — a disabled
 * button is drawn "only where the reason is shown beside it". `disabledReason`
 * is how the kit makes that cheap: it becomes the `title`, so the reason is at
 * least reachable from the control itself, and a page that has room states it
 * in words as well.
 *
 * =========================================================================
 * DESTRUCTIVE IS NOT A RED FILL
 * =========================================================================
 * White ground, `--err-edge` border, `--err` text (S2-09). The ticket screen's
 * own "Remove the assignment" already did this before the kit existed, and the
 * `ConfirmModal` disagreed with it in a filled `bg-red-600`. **The kit follows
 * the page here, not the other way round.** A red surface is what an app uses
 * when it wants the destructive button to be the easiest one to hit.
 *
 * =========================================================================
 * SIZES
 * =========================================================================
 * `md` is 36 px (`--ctl-h`), `sm` is 32 px — the floor from reference §4, not a
 * compact variant that quietly goes under it. There is no `xs`, on purpose:
 * the four `Change` links this phase replaced measured 16 px high.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { FOCUS_RING, HIT_TARGET } from './focus';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive';
export type ButtonSize = 'md' | 'sm';

const BASE =
    'inline-flex items-center justify-center gap-1.5 rounded-ctl font-medium ' +
    'transition-colors disabled:cursor-not-allowed ' + FOCUS_RING;

const SIZES: Record<ButtonSize, string> = {
    md: 'h-9 px-3 text-sm',
    sm: 'h-8 px-2.5 text-xs',
};

const ENABLED: Record<ButtonVariant, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    secondary: 'border border-hair bg-surface text-ink hover:bg-hover',
    quiet: 'text-info hover:bg-info-50',
    destructive: 'border border-err-edge bg-surface text-err hover:bg-err-50',
};

/**
 * ONE disabled appearance for all four variants. It is deliberately not
 * parameterised: the whole point of S2-08 is that a reader must be able to tell
 * a dead control from a live one without comparing it to its neighbour, and
 * four different dead appearances would put that back.
 */
const DISABLED = 'border border-transparent bg-head text-faint';

export function classesFor(variant: ButtonVariant, size: ButtonSize, disabled: boolean): string {
    return [BASE, SIZES[size], disabled ? DISABLED : ENABLED[variant]].join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Why this button cannot be pressed. Becomes the `title` (reference §7). */
    disabledReason?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    { variant = 'secondary', size = 'md', disabled = false, disabledReason, className = '', type = 'button', title, children, ...rest },
    ref,
) {
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled}
            title={disabled ? (disabledReason ?? title) : title}
            className={`${classesFor(variant, size, disabled)} ${className}`.trim()}
            {...rest}
        >
            {children}
        </button>
    );
});

/**
 * A button whose whole content is an icon. `label` is not optional — an icon
 * button without an accessible name is a control a screen reader announces as
 * "button", and there were 16×16 ones on every column header.
 */
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
    label: string;
    children: ReactNode;
    /**
     * `inherit` sets no colour at all — for an icon button that sits inside
     * something already tinted, such as the dismiss cross on a toast, where
     * `--info` would be the one foreign colour on the card.
     */
    variant?: ButtonVariant | 'inherit';
    /** Set when the control is currently doing something — the funnel that filters. */
    active?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
    { label, children, variant = 'quiet', active = false, disabled = false, className = '', type = 'button', title, ...rest },
    ref,
) {
    const tone = disabled
        ? DISABLED
        : active
            ? 'text-info hover:bg-info-50'
            : variant === 'inherit'
                ? 'hover:bg-black/5'
                : ENABLED[variant];
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled}
            aria-label={label}
            title={title ?? label}
            className={`inline-flex items-center justify-center rounded-ctl ${HIT_TARGET} ${tone} ${FOCUS_RING} disabled:cursor-not-allowed ${className}`.trim()}
            {...rest}
        >
            {children}
        </button>
    );
});

/**
 * The class a LINK wears when it acts like a quiet button.
 *
 * A `<Link>` or an `<a>` cannot become a `<button>` — a destination is not an
 * action, and turning one into the other breaks the middle click, the context
 * menu and "open in a new tab". So the colour is shared instead of the element:
 * this is the one place `--info` is written for an inline link, which is what
 * `design.md` §17's third deviation row asked P36 to provide. The 33 stock-blue
 * sites were re-pointed at the same token in the same phase.
 */
export const TEXT_LINK = `rounded-ctl text-info hover:underline ${FOCUS_RING}`;
